import {objectFromEntries} from '@benchling/js-utils';
import {Edge, Node, NodeOrigin} from 'reactflow';

import {DEFAULT_LAYOUT_ALGORITHM, LayoutAlgorithmName} from './layoutAlgorithmConstants';

export type ElkLayoutDirection = 'RIGHT' | 'DOWN';
export const DEFAULT_ELK_LAYOUT_DIRECTION: ElkLayoutDirection = 'RIGHT';

/**
 * Settings shared by layout algorithms.
 */
interface BaseLayoutSettings<N extends Node, E extends Edge> {
  calculateNodeDimensions: (node: N) => {height: number; width: number};
  calculateEdgeLabelDimensions: (edge: E) => {height: number; width: number};
  horizontalSpaceBetweenNodes: number;
  verticalSpaceBetweenNodes: number;
  nodeOrigin: NodeOrigin;
  snapToGrid?: [number, number];
  // If set, and if the layout algorithm supports it, these nodes will be forced to appear at the start
  // and end of the layout.
  startNodeId?: string;
  endNodeId?: string;
}

export interface DagreLayoutSettings<N extends Node, E extends Edge> extends BaseLayoutSettings<N, E> {
  algorithm: LayoutAlgorithmName.DAGRE;
}

export interface ElkLayoutSettings<N extends Node, E extends Edge> extends BaseLayoutSettings<N, E> {
  algorithm: LayoutAlgorithmName.ELK;
  elkDirection: ElkLayoutDirection;
  elkLayoutOptions?: Record<string, string>;
}

/**
 * Settings for arranging a flowchart.
 */
export type LayoutSettings<N extends Node, E extends Edge> =
  | DagreLayoutSettings<N, E>
  | ElkLayoutSettings<N, E>;

export type CustomLayoutSettings<N extends Node, E extends Edge> =
  | (Partial<BaseLayoutSettings<N, E>> & {
      algorithm?: LayoutAlgorithmName.DAGRE;
    })
  | (Partial<BaseLayoutSettings<N, E>> & {
      algorithm: LayoutAlgorithmName.ELK;
      elkDirection?: ElkLayoutDirection;
      elkLayoutOptions?: Record<string, string>;
    });

/**
 * Default settings for arranging a flowchart.
 *
 * The default settings:
 * • Use nodes `height` and `width`, as auto-calculated by `reactflow`
 * • Does not assign dimensions for edge labels
 * • Spaces nodes 100 pixels apart
 * • Node positions returned by the layout represent the top-left position of each node
 *
 * @package
 */
export const DEFAULT_LAYOUT_SETTINGS: LayoutSettings<Node, Edge> = {
  /** Use nodes' heights and widths that were automatically calculated by `reactflow`. */
  calculateNodeDimensions: (node) => ({height: node.height ?? 0, width: node.width ?? 0}),
  // TODO(BNCH-125315): Automatically calculate edge labels' dimensions
  calculateEdgeLabelDimensions: () => ({height: 0, width: 0}),
  horizontalSpaceBetweenNodes: 100,
  verticalSpaceBetweenNodes: 100,
  nodeOrigin: [0, 0],
  algorithm: DEFAULT_LAYOUT_ALGORITHM,
};

/**
 * Apply partial custom settings over the default layout settings.
 *
 * @package
 */
export function applyCustomLayoutSettings<N extends Node, E extends Edge>(
  customLayoutSettings: CustomLayoutSettings<N, E> = {}
): LayoutSettings<N, E> {
  const layoutSettings = {
    calculateNodeDimensions:
      customLayoutSettings.calculateNodeDimensions ??
      (DEFAULT_LAYOUT_SETTINGS.calculateNodeDimensions as (node: N) => {height: number; width: number}),
    calculateEdgeLabelDimensions:
      customLayoutSettings.calculateEdgeLabelDimensions ??
      (DEFAULT_LAYOUT_SETTINGS.calculateEdgeLabelDimensions as (edge: E) => {
        height: number;
        width: number;
      }),
    horizontalSpaceBetweenNodes:
      customLayoutSettings.horizontalSpaceBetweenNodes ?? DEFAULT_LAYOUT_SETTINGS.horizontalSpaceBetweenNodes,
    verticalSpaceBetweenNodes:
      customLayoutSettings.verticalSpaceBetweenNodes ?? DEFAULT_LAYOUT_SETTINGS.verticalSpaceBetweenNodes,
    nodeOrigin: customLayoutSettings.nodeOrigin ?? DEFAULT_LAYOUT_SETTINGS.nodeOrigin,
    snapToGrid: customLayoutSettings.snapToGrid,
    startNodeId: customLayoutSettings.startNodeId,
    endNodeId: customLayoutSettings.endNodeId,
  };
  if (customLayoutSettings.algorithm === LayoutAlgorithmName.ELK) {
    return {
      ...layoutSettings,
      algorithm: LayoutAlgorithmName.ELK,
      elkDirection: customLayoutSettings.elkDirection ?? DEFAULT_ELK_LAYOUT_DIRECTION,
      elkLayoutOptions: customLayoutSettings.elkLayoutOptions,
    };
  }

  return {
    ...layoutSettings,
    algorithm: DEFAULT_LAYOUT_ALGORITHM,
  };
}

/**
 * Calculated dimensions to layout a flowchart.
 *
 * See `LayoutSettings` and `calculateDimensionsForLayout` to calculate these dimensions.
 *
 * @package
 */
export interface DimensionsForLayout {
  /** Map from nodes' IDs to dimensions. A dimension must be calculated for each node. */
  nodes: {[nodeId: string]: {height: number; width: number}};
  /** Map from edges' IDs to their labels' dimensions. A dimension must be calculated for each edge. */
  edges: {[edgeId: string]: {height: number; width: number}};
  horizontalSpaceBetweenNodes: number;
  verticalSpaceBetweenNodes: number;
  nodeOrigin: NodeOrigin;
  snapToGrid?: [number, number];
  // If set, and if the layout algorithm supports it, these nodes will be forced to appear at the start
  // and end of the layout.
  startNodeId?: string;
  endNodeId?: string;
  elkDirection?: ElkLayoutDirection;
  elkLayoutOptions?: Record<string, string>;
}

/**
 * Calculate all dimensions required by `layoutFlowchart`.
 *
 * @package
 */
export default function calculateDimensionsForLayout<N extends Node, E extends Edge>(
  nodes: Array<N>,
  edges: Array<E>,
  {
    calculateNodeDimensions,
    calculateEdgeLabelDimensions,
    algorithm: _algorithm,
    ...layoutSettings
  }: LayoutSettings<N, E>
): DimensionsForLayout {
  return {
    nodes: objectFromEntries(nodes.map((node) => [node.id, calculateNodeDimensions(node)])),
    edges: objectFromEntries(edges.map((edge) => [edge.id, calculateEdgeLabelDimensions(edge)])),
    ...layoutSettings,
  };
}
